/**
 * MySubscriptionModal — User-facing modal for "Mi Suscripción"
 *
 * Flow:
 *  1. Main view: plan info + button "Administrar suscripción"
 *  2. Click → "Antes que te vayas..." retention view with reason selector
 *  3. Submit reason → if offer exists, show retention offer view
 *     - User accepts → apply coupon/pause/support, show success
 *     - User declines → redirect to Stripe Customer Portal
 *  4. If no offer (found alternative / other) → straight to Stripe Portal
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, CreditCard, Calendar, Loader2, ExternalLink, Heart,
  DollarSign, MoonStar, MessageCircle, Search, AlertTriangle
} from 'lucide-react';

const REASONS = [
  { id: 'too_expensive', icon: DollarSign, label: 'Es muy caro para mí', sub: 'Cuesta más de lo que puedo justificar' },
  { id: 'not_using', icon: MoonStar, label: 'No lo estoy usando lo suficiente', sub: 'No descargo tantos tracks últimamente' },
  { id: 'found_alternative', icon: Search, label: 'Encontré otro pool', sub: 'Estoy probando otra plataforma' },
  { id: 'technical_issue', icon: AlertTriangle, label: 'Tuve un problema técnico', sub: 'Algo no funciona como esperaba' },
  { id: 'other', icon: MessageCircle, label: 'Otro motivo', sub: 'Te lo cuento en un comentario' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('es-ES', {
      day: '2-digit', month: 'long', year: 'numeric',
    });
  } catch { return iso; }
};

const MySubscriptionModal = ({ onClose }) => {
  const [view, setView] = useState('main'); // main | reasons | offer | success | bye
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [comment, setComment] = useState('');
  const [alternativeName, setAlternativeName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedbackId, setFeedbackId] = useState(null);
  const [offer, setOffer] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/subscriptions/my-status');
      setStatus(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const goToPortal = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/subscriptions/create-portal');
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert('No se pudo abrir el portal de facturación.');
      }
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const submitReason = async () => {
    if (!selectedReason) return;
    setSubmitting(true);
    try {
      const res = await api.post('/subscriptions/cancellation-feedback', {
        reason: selectedReason,
        comment: comment.trim(),
        alternative_name: alternativeName.trim(),
      });
      setFeedbackId(res.data.feedback_id);
      if (res.data.offer) {
        setOffer(res.data.offer);
        setView('offer');
      } else {
        // No retention offer → go straight to Stripe Portal so they can cancel
        await goToPortal();
      }
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const acceptOffer = async () => {
    if (!offer || !feedbackId) return;
    setSubmitting(true);
    try {
      const res = await api.post('/subscriptions/accept-retention-offer', {
        feedback_id: feedbackId,
        offer_type: offer.type,
      });
      setSuccessMsg(res.data.message || '¡Listo!');
      setView('success');
      // For support type, open mailto
      if (offer.type === 'support') {
        setTimeout(() => {
          window.location.href = `mailto:soporte@wtplpool.com?subject=Necesito%20ayuda%20con%20WTPL&body=${encodeURIComponent(comment || '')}`;
        }, 1500);
      }
    } catch (e) {
      alert('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const declineOffer = async () => {
    // User said no to retention → take them to Stripe Portal to actually cancel
    await goToPortal();
  };

  const statusBadge = () => {
    const s = status?.subscription_status;
    if (status?.is_admin) return { text: 'ADMIN', color: 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30' };
    if (s === 'active') return { text: 'ACTIVA', color: 'bg-[#00D9FF]/15 text-[#00D9FF] border-[#00D9FF]/30' };
    if (s === 'past_due') return { text: 'PAGO ATRASADO', color: 'bg-[#FF9900]/15 text-[#FF9900] border-[#FF9900]/30' };
    if (s === 'cancelled') return { text: 'CANCELADA', color: 'bg-[#1A1A1A] text-[#888] border-[#333]' };
    return { text: 'FREE', color: 'bg-[#1A1A1A] text-[#888] border-[#333]' };
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="my-subscription-modal"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-xl w-full max-w-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#1A1A1A] flex items-center justify-between">
          <h2 className="text-base font-bold text-white tracking-tight flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-[#00D9FF]" />
            Mi Suscripción
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-sm bg-[#141414] border border-[#262626] flex items-center justify-center text-[#888] hover:text-white"
            data-testid="close-subscription-modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 text-[#00D9FF] animate-spin" />
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {/* ============ MAIN VIEW ============ */}
              {view === 'main' && (
                <motion.div
                  key="main"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4"
                >
                  <div className="bg-[#0D0D0D] border border-[#1A1A1A] rounded-lg p-5">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-[#52525B]">Estado</span>
                      <span className={`px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-wider border ${statusBadge().color}`} data-testid="status-badge">
                        {statusBadge().text}
                      </span>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-[#888]">Plan</span>
                        <span className="text-sm font-bold text-white">
                          ${status?.plan_amount?.toFixed(2) || '4.99'} / {status?.plan_interval === 'year' ? 'año' : 'mes'}
                        </span>
                      </div>
                      {status?.next_charge && (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-[#888]">Próximo cobro</span>
                          <span className="text-sm text-white flex items-center gap-1.5">
                            <Calendar className="w-3 h-3 text-[#00D9FF]" />
                            {formatDate(status.next_charge)}
                          </span>
                        </div>
                      )}
                      {status?.cancel_at_period_end && (
                        <div className="bg-[#00D9FF]/5 border border-[#00D9FF]/20 rounded-md p-3 text-xs text-[#00D9FF] flex items-center gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                          Tu suscripción se cancelará al final del periodo actual.
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-[#666] leading-relaxed">
                    Desde &quot;Administrar suscripción&quot; podés actualizar tu tarjeta, descargar facturas,
                    pausar o cancelar. Si querés cancelar, primero queremos ofrecerte una alternativa.
                  </div>

                  <div className="flex flex-col gap-2">
                    {status?.has_stripe_subscription && (
                      <button
                        onClick={goToPortal}
                        disabled={submitting}
                        data-testid="open-stripe-portal"
                        className="w-full px-4 py-3 bg-[#141414] border border-[#262626] hover:border-[#00D9FF]/40 text-white text-sm font-mono uppercase tracking-wider rounded-md flex items-center justify-center gap-2 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Portal de facturación
                      </button>
                    )}
                    {status?.subscription_status === 'active' && !status?.cancel_at_period_end && (
                      <button
                        onClick={() => setView('reasons')}
                        data-testid="start-cancel-flow"
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#262626] text-[#888] text-sm hover:text-white rounded-md transition-colors"
                      >
                        Quiero cancelar mi suscripción
                      </button>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ============ REASONS VIEW ============ */}
              {view === 'reasons' && (
                <motion.div
                  key="reasons"
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  className="space-y-4"
                >
                  <div className="flex items-start gap-3">
                    <Heart className="w-5 h-5 text-[#00D9FF] flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="text-base font-bold text-white mb-1">Antes de que te vayas…</h3>
                      <p className="text-xs text-[#888]">
                        Cuéntanos qué te hizo decidir cancelar. Tu respuesta nos ayuda a mejorar el pool
                        — y quizás tenemos algo para vos.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    {REASONS.map((r) => {
                      const sel = selectedReason === r.id;
                      return (
                        <button
                          key={r.id}
                          onClick={() => setSelectedReason(r.id)}
                          data-testid={`reason-${r.id}`}
                          className={`w-full flex items-center gap-3 p-3 rounded-md border text-left transition-colors ${
                            sel
                              ? 'bg-[#00D9FF]/10 border-[#00D9FF]/40'
                              : 'bg-[#0D0D0D] border-[#1A1A1A] hover:border-[#262626]'
                          }`}
                        >
                          <r.icon className={`w-4 h-4 flex-shrink-0 ${sel ? 'text-[#00D9FF]' : 'text-[#666]'}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium ${sel ? 'text-white' : 'text-[#B3B3B3]'}`}>{r.label}</p>
                            <p className="text-[11px] text-[#555]">{r.sub}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Conditional fields */}
                  {selectedReason === 'found_alternative' && (
                    <input
                      value={alternativeName}
                      onChange={(e) => setAlternativeName(e.target.value)}
                      placeholder="¿Cuál pool? (opcional)"
                      className="w-full px-3 py-2 bg-[#0D0D0D] border border-[#262626] text-sm text-white rounded-md placeholder:text-[#444] focus:outline-none focus:border-[#00D9FF]/40"
                    />
                  )}
                  {(selectedReason === 'technical_issue' || selectedReason === 'other') && (
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={selectedReason === 'technical_issue' ? 'Cuéntanos qué pasó…' : 'Tu comentario…'}
                      rows={3}
                      className="w-full px-3 py-2 bg-[#0D0D0D] border border-[#262626] text-sm text-white rounded-md placeholder:text-[#444] focus:outline-none focus:border-[#00D9FF]/40 resize-none"
                    />
                  )}

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => setView('main')}
                      className="flex-1 px-4 py-2.5 bg-[#0D0D0D] border border-[#262626] text-[#888] text-sm hover:text-white rounded-md"
                    >
                      Volver
                    </button>
                    <button
                      onClick={submitReason}
                      disabled={!selectedReason || submitting}
                      data-testid="submit-reason"
                      className="flex-1 px-4 py-2.5 bg-[#00D9FF] hover:bg-[#00C4E8] disabled:opacity-40 text-black text-sm font-bold rounded-md flex items-center justify-center gap-2"
                    >
                      {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                      Continuar
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ============ OFFER VIEW ============ */}
              {view === 'offer' && offer && (
                <motion.div
                  key="offer"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-4 text-center"
                >
                  <div className="mx-auto w-14 h-14 rounded-full bg-[#00D9FF]/10 border border-[#00D9FF]/30 flex items-center justify-center">
                    <Heart className="w-7 h-7 text-[#00D9FF]" />
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#00D9FF] font-mono font-bold mb-2">Oferta exclusiva</p>
                    <h3 className="text-xl font-bold text-white mb-2">{offer.label}</h3>
                    <p className="text-sm text-[#B3B3B3] leading-relaxed">{offer.details}</p>
                  </div>
                  <div className="flex flex-col gap-2 pt-2">
                    <button
                      onClick={acceptOffer}
                      disabled={submitting}
                      data-testid="accept-offer"
                      className="w-full px-4 py-3 bg-[#00D9FF] hover:bg-[#00C4E8] disabled:opacity-40 text-black text-sm font-bold rounded-md flex items-center justify-center gap-2"
                    >
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Heart className="w-4 h-4" />}
                      {offer.action_label}
                    </button>
                    <button
                      onClick={declineOffer}
                      disabled={submitting}
                      data-testid="decline-offer"
                      className="w-full px-4 py-2.5 bg-transparent text-[#666] text-xs hover:text-white"
                    >
                      No gracias, quiero cancelar igual
                    </button>
                  </div>
                </motion.div>
              )}

              {/* ============ SUCCESS VIEW ============ */}
              {view === 'success' && (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6 space-y-4"
                >
                  <div className="mx-auto w-16 h-16 rounded-full bg-[#00D9FF]/15 border border-[#00D9FF]/40 flex items-center justify-center">
                    <svg className="w-8 h-8 text-[#00D9FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/>
                    </svg>
                  </div>
                  <h3 className="text-lg font-bold text-white">{successMsg}</h3>
                  <button
                    onClick={onClose}
                    data-testid="success-close"
                    className="px-6 py-2.5 bg-[#141414] border border-[#262626] hover:border-[#00D9FF]/40 text-white text-sm font-mono uppercase tracking-wider rounded-md"
                  >
                    Cerrar
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default MySubscriptionModal;
